import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  MicrosoftOutlookLogo,
  CalendarBlank,
  CheckCircle,
  WarningCircle,
  ArrowsClockwise,
  PlugsConnected,
  Plugs,
} from "@phosphor-icons/react";

/**
 * Owner-dashboard card for the Outlook (Microsoft Graph) two-way sync.
 * Mirrors GoogleCalendarCard's state machine and UX so owners get a
 * consistent feel across both integrations.
 *
 * States:
 *  - feature_disabled  → hide entirely
 *  - pro_required      → upsell card
 *  - not connected     → connect buttons (dedicated / existing)
 *  - needs_calendar_selection → picker list
 *  - connected         → status + sync now + disconnect + pending inbox
 */
function MissingFieldsList({ missing }) {
  const labels = {
    title: "Title",
    address: "Address",
    address_incomplete: "Full street address (street + city/state or ZIP)",
    address_unrecognized: "Address (couldn't be located)",
    start: "Start time",
    end: "End time",
  };
  return (
    <ul className="mt-1 text-[11px] text-red-700 dark:text-red-300 flex flex-wrap gap-x-2 gap-y-0.5">
      {missing.map((m) => (
        <li key={m} className="inline-flex items-center gap-1">
          <WarningCircle weight="fill" className="h-3 w-3" />
          <span>{labels[m] || m}</span>
        </li>
      ))}
    </ul>
  );
}

export default function OutlookCalendarCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async () => {
    let data;
    try {
      const r = await api.get("/integrations/outlook/status");
      data = r.data;
      setStatus(data);
    } catch (_) {
      setStatus({ enabled: true, connected: false });
      setLoading(false);
      return;
    }
    if (data?.connected) {
      try {
        const p = await api.get("/integrations/outlook/pending");
        setPending(p.data?.pending || []);
      } catch (_) {
        setPending([]);
      }
      if (data.needs_calendar_selection) {
        try {
          const c = await api.get("/integrations/outlook/calendars");
          setCalendars(c.data?.calendars || []);
        } catch (_) {
          setCalendars([]);
        }
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Surface OAuth callback result if we just came back from Microsoft.
      const params = new URLSearchParams(window.location.search);
      const outlookFlag = params.get("outlook");
      if (outlookFlag === "connected") {
        toast.success("Outlook Calendar connected.");
      } else if (outlookFlag === "error") {
        toast.error(`Outlook connection failed: ${params.get("reason") || "unknown"}`);
      }
      if (outlookFlag) {
        params.delete("outlook");
        params.delete("reason");
        const next = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (next ? `?${next}` : ""));
      }
      if (!cancelled) await load();
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !status) return null;

  if (!status.enabled && status.reason === "feature_disabled") return null;

  // Free owners — upsell
  if (!status.enabled && status.reason === "pro_required") {
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="outlook-card-upsell">
        <div className="flex items-center gap-2 mb-1">
          <MicrosoftOutlookLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Connect Outlook Calendar</h2>
          <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 h-5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[9px] uppercase tracking-wider font-bold">
            Pro
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          Sync your Outlook (personal or Microsoft 365) calendar with TruckTrackr in both directions. Available on Pro.
        </p>
      </section>
    );
  }

  // Not connected — Pro owner, ready to start
  if (!status.connected) {
    const connect = async (kind) => {
      setBusy(true);
      try {
        const { data } = await api.post("/integrations/outlook/start", { calendar_kind: kind });
        window.location.href = data.authorization_url;
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't start the Outlook connect flow"));
        setBusy(false);
      }
    };
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="outlook-card-connect">
        <div className="flex items-center gap-2 mb-1">
          <MicrosoftOutlookLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Connect Outlook Calendar</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Works with both <strong className="text-foreground">personal Outlook.com</strong> and{" "}
          <strong className="text-foreground">Microsoft 365</strong> accounts. Add events to your Outlook calendar with
          a <strong className="text-foreground">title</strong>,{" "}
          <strong className="text-foreground">full address</strong>, and{" "}
          <strong className="text-foreground">start + end time</strong> — they auto-publish to TruckTrackr. Edits made
          on TruckTrackr push back to Outlook.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={() => connect("dedicated")}
            disabled={busy}
            data-testid="outlook-connect-dedicated"
            className="flex-1"
          >
            <PlugsConnected size={16} weight="bold" className="mr-1.5" />
            Create a dedicated &ldquo;TruckTrackr Schedule&rdquo; calendar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => connect("existing")}
            disabled={busy}
            data-testid="outlook-connect-existing"
            className="flex-1"
          >
            <CalendarBlank size={16} weight="bold" className="mr-1.5" />
            Pick an existing calendar
          </Button>
        </div>
        <div className="text-[11px] text-muted-foreground mt-3">
          Dedicated keeps personal events private. Existing is best if you already manage your truck schedule in a
          specific Outlook calendar.
        </div>
      </section>
    );
  }

  // Needs calendar selection
  if (status.needs_calendar_selection) {
    const pick = async (calId) => {
      setBusy(true);
      try {
        await api.post("/integrations/outlook/select-calendar", { calendar_id: calId });
        toast.success("Calendar linked. First sync running…");
        await new Promise((r) => setTimeout(r, 1500));
        await load();
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't select that calendar"));
      } finally {
        setBusy(false);
      }
    };
    const startOver = async () => {
      if (!window.confirm("Disconnect and start over?")) return;
      setBusy(true);
      try {
        await api.post("/integrations/outlook/disconnect");
        toast.success("Disconnected.");
        await load();
      } catch (e) {
        toast.error(formatApiError(e, "Disconnect failed"));
      } finally {
        setBusy(false);
      }
    };
    return (
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="outlook-card-select-calendar">
        <div className="flex items-center gap-2 mb-2">
          <MicrosoftOutlookLogo weight="duotone" size={20} className="text-primary" />
          <h2 className="font-display font-semibold text-lg tracking-tight">Pick a calendar</h2>
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-bold">
            <CheckCircle weight="fill" className="h-3 w-3" />
            Connected
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          You&apos;re connected as <strong className="text-foreground">{status.outlook_email}</strong>. Pick which Outlook
          calendar should drive your TruckTrackr schedule.
        </p>
        {calendars.length === 0 ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="text-sm font-semibold text-foreground mb-1">Couldn&apos;t load your calendars.</div>
            <p className="text-[12px] text-muted-foreground mb-3">
              Disconnect and reconnect using the dedicated calendar option — TruckTrackr will create a new &ldquo;TruckTrackr
              Schedule&rdquo; calendar in your Outlook account.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={startOver} disabled={busy} data-testid="outlook-disconnect-fallback">
                <Plugs size={14} weight="bold" className="mr-1" />
                Disconnect &amp; start over
              </Button>
              <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="outlook-retry-calendars">
                <ArrowsClockwise size={14} weight="bold" className="mr-1" />
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {calendars.map((c) => (
              <li key={c.id} className="flex items-center gap-3 py-2">
                <CalendarBlank weight="duotone" className="h-5 w-5 text-primary flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">
                    {c.summary}{" "}
                    {c.primary && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-1">primary</span>
                    )}
                  </div>
                  {c.owner_name && <div className="text-[11px] text-muted-foreground">{c.owner_name}</div>}
                </div>
                <Button size="sm" onClick={() => pick(c.id)} disabled={busy} data-testid={`outlook-pick-${c.id}`}>
                  Use this
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  // Fully connected
  const lastSynced = status.last_synced_at ? new Date(status.last_synced_at).toLocaleString() : "Never";
  const syncOk = status.last_sync_status === "ok";
  const syncRevoked = status.last_sync_status === "revoked";

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/outlook/sync-now");
      const s = data.stats || {};
      toast.success(`Synced. Published ${s.published || 0}, needs fix ${s.pending || 0}.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Sync failed"));
    } finally {
      setBusy(false);
    }
  };

  const retryProvisionDedicated = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/outlook/provision-dedicated");
      toast.success(`Dedicated calendar created: ${data.calendar_summary}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't create the dedicated calendar"));
    } finally {
      setBusy(false);
    }
  };

  const missingCalendar = !status.calendar_id;

  const disconnect = async () => {
    if (!window.confirm("Disconnect Outlook? Synced events will be removed from your TruckTrackr schedule.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/outlook/disconnect");
      toast.success("Disconnected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Disconnect failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="outlook-card-connected">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center flex-none">
          <MicrosoftOutlookLogo weight="duotone" size={20} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display font-semibold text-lg tracking-tight">Outlook Calendar</h2>
            {syncRevoked ? (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-red-500/15 text-red-600 dark:text-red-300 text-[10px] uppercase tracking-wider font-bold">
                <WarningCircle weight="fill" className="h-3 w-3" />
                Reconnect required
              </span>
            ) : syncOk ? (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-[10px] uppercase tracking-wider font-bold">
                <CheckCircle weight="fill" className="h-3 w-3" />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 rounded-full bg-muted text-muted-foreground text-[10px] uppercase tracking-wider font-bold">
                Pending first sync
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {status.outlook_email} ·{" "}
            <span className="font-medium">{status.calendar_summary || status.calendar_id}</span>
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Last synced: {lastSynced}</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button size="sm" onClick={syncNow} disabled={busy || missingCalendar} data-testid="outlook-sync-now">
            <ArrowsClockwise size={14} weight="bold" className="mr-1" />
            {busy ? "Syncing…" : "Sync now"}
          </Button>
          <Button size="sm" variant="outline" onClick={disconnect} disabled={busy} data-testid="outlook-disconnect">
            <Plugs size={14} weight="bold" className="mr-1" />
            Disconnect
          </Button>
        </div>
      </div>

      {missingCalendar && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="outlook-calendar-missing">
          <div className="flex items-center gap-2 mb-1">
            <WarningCircle weight="duotone" className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div className="text-sm font-semibold text-foreground">No calendar linked yet</div>
          </div>
          <p className="text-[12px] text-muted-foreground mb-3">
            We couldn&apos;t create your &ldquo;TruckTrackr Schedule&rdquo; calendar in Outlook. Click Retry to try again.
          </p>
          <Button size="sm" onClick={retryProvisionDedicated} disabled={busy} data-testid="outlook-retry-provision">
            <ArrowsClockwise size={14} weight="bold" className="mr-1" />
            Retry calendar setup
          </Button>
        </div>
      )}

      {pending.length > 0 && (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="outlook-pending-inbox">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle weight="duotone" className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <div className="text-xs font-semibold text-foreground">
              {pending.length} event{pending.length === 1 ? "" : "s"} need
              {pending.length === 1 ? "s" : ""} fixing
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3">
            These events are in your Outlook calendar but missing required info. Open the event in Outlook, add the
            missing fields, and the next sync (every 5 min) will publish it.
          </p>
          <ul className="divide-y divide-amber-500/10">
            {pending.slice(0, 6).map((p) => (
              <li key={p.outlook_event_id} className="py-2" data-testid={`outlook-pending-${p.outlook_event_id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm truncate">{p.title || "(no title)"}</span>
                  {p.event_start && (
                    <span className="text-[11px] text-muted-foreground">
                      ·{" "}
                      {new Date(p.event_start).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                  )}
                  {p.html_link && (
                    <a
                      href={p.html_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-[11px] font-semibold text-primary hover:underline"
                    >
                      Fix in Outlook →
                    </a>
                  )}
                </div>
                <MissingFieldsList missing={p.missing_fields || []} />
              </li>
            ))}
            {pending.length > 6 && (
              <li className="py-2 text-[11px] text-muted-foreground text-center">+ {pending.length - 6} more</li>
            )}
          </ul>
        </div>
      )}
    </section>
  );
}
